import { useEffect, useState } from 'react';
import { ConnectionState } from 'livekit-client';

import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';

/** Mutually exclusive local journey labels for the student exam room. */
export type StudentRuntimeJourney =
  | 'moving_to_exam_room'
  | 'reconnecting'
  | 'waiting_for_examiner'
  | 'examiner_preparing'
  | 'lecturer_preparing'
  | 'pre_start_waiting'
  | 'in_exam_committed';

/** Pre-Q1 and Q1-active phases for the stable student exam shell (R3). */
export type StudentExamShellPhase =
  | 'waiting_for_examiner'
  | 'ready_to_start'
  | 'starting'
  | 'between_questions'
  | 'question_active';

export type DeriveStudentRuntimeJourneyInput = {
  sessionPhase: OralExamSessionPhase;
  runState: OralExamRunStateResponse['state'];
  conductPhase?: OralExamRunStateResponse['conductPhase'];
  connectionState: ConnectionState;
  isReconnecting: boolean;
};

function isActiveExamRoomPhase(
  sessionPhase: OralExamSessionPhase,
  runState: OralExamRunStateResponse['state'],
): boolean {
  if (sessionPhase !== OralExamSessionPhase.in_exam) return false;
  if (runState === 'completed' || runState === 'aborted') return false;
  return true;
}

function journeyFromConductPhase(
  conductPhase: OralExamConductPhase,
): StudentRuntimeJourney {
  switch (conductPhase) {
    case OralExamConductPhase.waiting_for_examiner:
      return 'waiting_for_examiner';
    case OralExamConductPhase.waiting_for_student:
    case OralExamConductPhase.starting_exam:
      return 'examiner_preparing';
    case OralExamConductPhase.in_exam:
      return 'in_exam_committed';
    default:
      return 'pre_start_waiting';
  }
}

/**
 * Derives the student's local runtime journey from backend ``conductPhase`` and
 * LiveKit transport signals only. Readiness timestamps and agent presence are
 * never interpreted as conduct state.
 *
 * Precedence (highest first):
 * 1. reconnecting — LiveKit Reconnecting / SignalReconnecting
 * 2. moving_to_exam_room — Disconnected / Connecting during the exam-room handoff
 * 3. backend ``conductPhase`` — canonical conduct projection
 * 4. pre_start_waiting — in_exam pre_start before the run begins
 */
export function deriveStudentRuntimeJourney(
  input: DeriveStudentRuntimeJourneyInput,
): StudentRuntimeJourney | null {
  const {
    sessionPhase,
    runState,
    conductPhase,
    connectionState,
    isReconnecting,
  } = input;

  if (!isActiveExamRoomPhase(sessionPhase, runState)) {
    if (sessionPhase === OralExamSessionPhase.in_exam && runState === 'pre_start') {
      return 'pre_start_waiting';
    }
    return null;
  }

  if (isReconnecting) {
    return 'reconnecting';
  }

  if (
    connectionState === ConnectionState.Disconnected
    || connectionState === ConnectionState.Connecting
  ) {
    return 'moving_to_exam_room';
  }

  if (conductPhase) {
    return journeyFromConductPhase(conductPhase);
  }

  if (runState === 'pre_start') {
    return 'pre_start_waiting';
  }

  return null;
}

/**
 * Derives the stable exam-shell phase from canonical backend ``conductPhase``.
 * ``currentQuestion`` is the orthogonal question-content signal once
 * ``conductPhase === in_exam``; timestamps are never interpreted as conduct state.
 */
export function deriveStudentExamShellPhase(
  conductPhase: OralExamConductPhase | null | undefined,
  currentQuestion: OralExamRunStateResponse['currentQuestion'],
  currentQuestionIndex: number | null | undefined = 0,
): StudentExamShellPhase {
  switch (conductPhase) {
    case OralExamConductPhase.waiting_for_student:
      return 'ready_to_start';
    case OralExamConductPhase.starting_exam:
      return 'starting';
    case OralExamConductPhase.in_exam:
      if (currentQuestion) return 'question_active';
      return (currentQuestionIndex ?? 0) > 0 ? 'between_questions' : 'starting';
    case OralExamConductPhase.waiting_for_examiner:
    default:
      return 'waiting_for_examiner';
  }
}

/** Only reveal the between-questions hold after this wait — faster advances keep the last card. */
export const BETWEEN_QUESTIONS_REVEAL_MS = 1000;

export function displayedExamShellPhase(params: {
  rawPhase: StudentExamShellPhase;
  hasHeldQuestion: boolean;
  holdElapsed: boolean;
}): StudentExamShellPhase {
  if (
    params.rawPhase === 'between_questions'
    && params.hasHeldQuestion
    && !params.holdElapsed
  ) {
    return 'question_active';
  }
  return params.rawPhase;
}

/**
 * Presentation delay for ``between_questions``. We cannot know the next-question
 * latency in advance; if a previous card is still in hand, keep it until the
 * wait actually exceeds {@link BETWEEN_QUESTIONS_REVEAL_MS}.
 */
export function useDisplayedExamShellPhase(
  rawPhase: StudentExamShellPhase,
  hasHeldQuestion: boolean,
  revealAfterMs: number = BETWEEN_QUESTIONS_REVEAL_MS,
): StudentExamShellPhase {
  const [holdElapsed, setHoldElapsed] = useState(false);

  useEffect(() => {
    if (rawPhase !== 'between_questions' || !hasHeldQuestion) {
      setHoldElapsed(false);
      return undefined;
    }
    if (revealAfterMs <= 0) {
      setHoldElapsed(true);
      return undefined;
    }
    const timer = setTimeout(() => setHoldElapsed(true), revealAfterMs);
    return () => clearTimeout(timer);
  }, [rawPhase, hasHeldQuestion, revealAfterMs]);

  return displayedExamShellPhase({ rawPhase, hasHeldQuestion, holdElapsed });
}

export function studentExamShellPhaseTestId(phase: StudentExamShellPhase): string {
  switch (phase) {
    case 'waiting_for_examiner':
      return 'oral-exam-student-shell-waiting';
    case 'ready_to_start':
      return 'oral-exam-student-shell-ready';
    case 'starting':
      return 'oral-exam-student-shell-starting';
    case 'between_questions':
      return 'oral-exam-student-between-questions';
    case 'question_active':
      return 'oral-exam-student-shell-question-active';
    default:
      return 'oral-exam-student-shell-unknown';
  }
}

export function shouldUseStudentExamShell(
  runState: Pick<OralExamRunStateResponse, 'mode' | 'sessionPhase' | 'state'>,
  runtimeJourney: StudentRuntimeJourney | null,
): boolean {
  if (runState.mode !== OralExamMode.one_to_n) return false;
  if (runState.sessionPhase !== OralExamSessionPhase.in_exam) return false;
  if (runState.state !== 'pre_start' && runState.state !== 'in_progress') return false;
  if (runtimeJourney === 'moving_to_exam_room') return false;
  return true;
}

export function studentRuntimeJourneyTestId(
  journey: StudentRuntimeJourney,
): string {
  switch (journey) {
    case 'moving_to_exam_room':
      return 'oral-exam-student-moving-to-exam-room';
    case 'reconnecting':
      return 'oral-exam-student-reconnecting';
    case 'waiting_for_examiner':
      return 'oral-exam-student-waiting-for-examiner';
    case 'examiner_preparing':
    case 'lecturer_preparing':
      return 'oral-exam-student-examiner-preparing';
    case 'pre_start_waiting':
      return 'oral-exam-student-waiting';
    case 'in_exam_committed':
      return 'oral-exam-student-in-progress';
    default:
      return 'oral-exam-student-unknown-journey';
  }
}
